import React, { useState, useEffect } from "react";
import Icon from "./Icon";
import * as api from "../../../services/api";

const PAGE_SIZE = 20; // Show 20 items at a time

function ActivityPanel({ show, onClose, selectedFile, showToast }) {
  const [allActivities, setAllActivities] = useState([]); // Full list from API
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE); // How many to show
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("recent");
  const [activeFile, setActiveFile] = useState(null);

  // When a file OR folder is selected, switch to that item's activity mode.
  // (Previously excluded folders, so "Activity Log" on a folder silently
  // stayed on whatever tab was already open instead of showing that
  // folder's activity.)
  useEffect(() => {
    if (selectedFile && selectedFile.id) {
      setActiveFile(selectedFile);
      setMode("file");
    }
  }, [selectedFile]);

  // Load data when panel opens or mode changes
  useEffect(() => {
    if (show) {
      setVisibleCount(PAGE_SIZE); // Reset on mode change
      if (mode === "recent") {
        loadRecentActivity();
      } else if (mode === "file" && activeFile && activeFile.id) {
        loadFileActivity(activeFile.id);
      }
    }
  }, [show, mode, activeFile]);

  const loadRecentActivity = async () => {
    setLoading(true);
    try {
      const res = await api.getRecentActivity();
      const logs = res.activity || [];
      const sorted = Array.isArray(logs)
        ? [...logs].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
        : [];
      setAllActivities(sorted);
    } catch (err) {
      setAllActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFileActivity = async (docId) => {
    if (!docId) return;
    setLoading(true);
    try {
      const res = await api.getActivity(docId);
      const logs = res.activity || res.activity_log || res.activities || [];
      const sorted = Array.isArray(logs)
        ? [...logs].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
        : [];
      setAllActivities(sorted);
    } catch (err) {
      setAllActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  };

  const visibleActivities = allActivities.slice(0, visibleCount);
  const hasMore = visibleCount < allActivities.length;
  const remainingCount = allActivities.length - visibleCount;

  const getActionIcon = (action) => {
    const icons = {
      created: "circlePlus",
      uploaded: "arrowUp",
      downloaded: "arrowDown",
      updated: "pen",
      deleted: "trash",
      shared: "share",
      viewed: "eye",
      renamed: "tag",
    };
    return icons[action] || "clock";
  };

  const getActionColor = (action) => {
    const colors = {
      created: "#34a853",
      uploaded: "#4285f4",
      downloaded: "#9c27b0",
      updated: "#ff9800",
      deleted: "#ea4335",
      shared: "#00bcd4",
    };
    return colors[action] || "#5f6368";
  };

  const getActionText = (action) => {
    const texts = {
      created: "created a file",
      uploaded: "uploaded a file",
      downloaded: "downloaded a file",
      updated: "updated a file",
      deleted: "deleted a file",
      shared: "changed permissions",
      viewed: "viewed a file",
      renamed: "renamed a file",
    };
    return texts[action] || action;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "";
      const now = new Date();
      const diff = now - date;
      if (diff < 60000) return "Just now";
      if (diff < 3600000) return Math.floor(diff / 60000) + " min ago";
      if (diff < 86400000) return Math.floor(diff / 3600000) + " hr ago";
      if (diff < 604800000) return Math.floor(diff / 86400000) + " days ago";
      return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  const handleRefresh = () => {
    setVisibleCount(PAGE_SIZE);
    if (mode === "recent") loadRecentActivity();
    else if (activeFile && activeFile.id) loadFileActivity(activeFile.id);
  };

  return (
    <div className={`ctodocx-activity-panel ${show ? "open" : ""}`}>
      <div className="activity-header">
        <h3><Icon name="clockHistory" /> Activity</h3>
        <button className="activity-close" onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>

      <div className="activity-tabs">
        <button
          className={`activity-tab ${mode === "recent" ? "active" : ""}`}
          onClick={() => { setMode("recent"); setVisibleCount(PAGE_SIZE); }}
        >
          All Activity
        </button>
        <button
          className={`activity-tab ${mode === "file" ? "active" : ""}`}
          onClick={() => { setMode("file"); setVisibleCount(PAGE_SIZE); }}
        >
          File Activity
        </button>
      </div>

      {mode === "file" && activeFile && (
        <div className="activity-file-info">
          <Icon name="file" style={{ color: "#5f6368" }} />
          <span>{activeFile.name}</span>
          <button className="activity-refresh-btn" onClick={handleRefresh} title="Refresh">
            <Icon name="refresh" />
          </button>
        </div>
      )}

      {mode === "file" && !activeFile && (
        <div className="activity-file-info">
          <Icon name="info" style={{ color: "#999" }} />
          <span style={{ color: "#999" }}>Click a file to view its activity</span>
        </div>
      )}

      {mode === "recent" && (
        <div className="activity-file-info">
          <Icon name="globe" style={{ color: "#4285f4" }} />
          <span>All activity ({allActivities.length} total)</span>
          <button className="activity-refresh-btn" onClick={handleRefresh} title="Refresh">
            <Icon name="refresh" />
          </button>
        </div>
      )}

      <div className="activity-list">
        {loading ? (
          <div className="activity-loading">
            <Icon name="spinner" spin />
            <span>Loading...</span>
          </div>
        ) : allActivities.length === 0 ? (
          <div className="activity-empty">
            <Icon name="inbox" />
            <p>{mode === "recent" ? "No activity yet" : (activeFile ? "No activity for this file" : "Select a file to view activity")}</p>
            <p className="activity-hint">Activity is logged on create, upload, download, share, and delete</p>
          </div>
        ) : (
          <>
            <div className="activity-timeline">
              {visibleActivities.map((a, i) => (
                <div className="activity-entry" key={i}>
                  <div className="activity-dot" style={{ background: getActionColor(a.action) }}>
                    <Icon name={getActionIcon(a.action)} />
                  </div>
                  <div className="activity-content">
                    <div className="activity-text">
                      <strong>{a.user || a.userName || a.user_name || "Unknown"}</strong>{" "}
                      <span className="activity-action-text">{getActionText(a.action)}</span>
                    </div>
                    {a.details && <div className="activity-detail">{a.details}</div>}
                    <div className="activity-time">{formatTime(a.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="activity-load-more">
                <button className="load-more-btn" onClick={handleLoadMore}>
                  <Icon name="chevronDown" />
                  <span>Load More ({remainingCount} remaining)</span>
                </button>
              </div>
            )}

            {/* All loaded indicator */}
            {!hasMore && allActivities.length > PAGE_SIZE && (
              <div className="activity-all-loaded">
                <Icon name="checkCircle" />
                <span>All {allActivities.length} activities loaded</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ActivityPanel;
